import React, { useState, useEffect } from "react";
import { Box, HStack, Pressable, ScrollView, Text } from "native-base";
import { useNavigate } from "react-router-dom";
import { setEntitydata } from "../../Redux/Actions/userActions";
import { useSelector, RootStateOrAny, useDispatch } from "react-redux";
import * as AV from "../../AVCore/AVCore";
import { Loc } from "../../App/Loc";
import EntityProfilePic from "../../components/common/EntityProfilePic";

type ExtendedEntity = AV.IAVEntity & {
  teams?: AV.ITypeLink[];
  scoreType?: string;
  participants?: AV.IParticipantLink[];
};

interface Props {
  view: string;
  passEntity: ExtendedEntity;
  leagueGameEntity: ExtendedEntity;
}

const Attendance = ({ view, passEntity, leagueGameEntity }: Props) => {
  const navigate = useNavigate();

  const [isAttending, setIsAttending] = useState(false);
  const [leagueGameInfo, setLeagueGameInfo] =
    useState<AV.ILeagueGameInfo | null>(null);
  const [attendingPlayers, setAttendingPlayers] = useState<
    AV.IParticipantLink[]
  >([]);
  const [notAttendingPlayers, setNotAttendingPlayers] = useState<
    AV.IParticipantLink[]
  >([]);

  useEffect(() => {
    if (passEntity?.type !== "LeagueGameBase") return;

    const loadLeagueGameInfo = async () => {
      const data = await AV.Game.getLeagueGameInfo({
        source: AV.AVEntity.getFk(passEntity),
        scoreType: passEntity.scoreType ?? "",
      });

      setLeagueGameInfo(data);
    };

    loadLeagueGameInfo();
  }, [passEntity?.id]);

  useEffect(() => {
    const loadAttendance = async () => {
      if (
        passEntity?.type !== "LeagueGameBase" &&
        leagueGameEntity?.status !== "Started"
      ) {
        setIsAttending(false);
        setAttendingPlayers([]);
        setNotAttendingPlayers([]);
        return;
      }

      if (passEntity?.type === "LeagueGameBase") {
        if (!leagueGameInfo) return;
        const teamLink = passEntity.teams?.[view === "right" ? 1 : 0];
        const teamInfo = leagueGameInfo.teams.find(
          (x) => x.team.id === teamLink?.id,
        );

        setAttendingPlayers(
          teamInfo?.participants?.filter((p) => p.status === "Yes") ?? [],
        );

        setNotAttendingPlayers(
          teamInfo?.participants?.filter(
            (p) =>
              p.status === undefined ||
              p.status === "No" ||
              p.status === "RSVP",
          ) ?? [],
        );

        setIsAttending(true);
      } else {
        const attendingPlayers =
          passEntity?.participants?.filter((p) => p?.status === "Yes") || [];

        const notAttendingPlayers =
          passEntity?.participants?.filter(
            (p) =>
              p?.status === undefined ||
              p?.status === "No" ||
              p?.status === "RSVP",
          ) || [];

        setAttendingPlayers(attendingPlayers);
        setNotAttendingPlayers(notAttendingPlayers);
        setIsAttending(true);
      }
    };

    loadAttendance();
  }, [passEntity, leagueGameEntity, leagueGameInfo]);

  const renderPlayers = (players: any[], emptyText: string) => {
    if (players.length === 0) {
      return (
        <Text fontSize="13px" color="#9CA3AF" textAlign="center" py="12px">
          {emptyText}
        </Text>
      );
    }

    return players.map((item, index) => (
      <HStack key={index} alignItems="center" mb="18px">
        <Pressable
          flexDirection="row"
          alignItems="center"
          onPress={() =>
            navigate(`/profile/${item?.id}/${item?.region}`, {
              state: {
                user: {
                  id: item?.id,
                  pk: item?.pk,
                  region: item?.region,
                  type: item?.type,
                  name: item?.title,
                },
              },
            })
          }
        >
          {!item?.jerseyNumber ? (
            <Box
              width="44px"
              height="44px"
              borderRadius="full"
              bg="coolGray.200"
              justifyContent="center"
              alignItems="center"
              overflow="hidden"
            >
              <EntityProfilePic width={44} height={44} link={item} />
            </Box>
          ) : (
            <Box
              width="46px"
              height="46px"
              borderRadius="999px"
              bg="#E52421"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="white" fontWeight="700" fontSize="18px">
                {item?.jerseyNumber}
              </Text>
            </Box>
          )}

          <Box ml="14px">
            <Text fontSize="14px" fontWeight="700" color="#111827">
              {item?.title}
            </Text>

            <Text fontSize="13px" color="#6B7280" mt="2px">
              {item?.position ?? ""}
            </Text>
          </Box>
        </Pressable>
      </HStack>
    ));
  };

  return (
    <>
      {/* HEADER */}
      <Box
        px="20px"
        pt="18px"
        pb="14px"
        borderBottomWidth={1}
        borderBottomColor="#F1F5F9"
      >
        <Text fontSize="22px" fontWeight="600" color="AVColor.primary">
          {passEntity?.type === "LeagueGameBase"
            ? passEntity?.teams?.[view === "right" ? 1 : 0]?.title
            : passEntity?.parent?.title}
        </Text>
      </Box>

      {/* BODY */}
      <ScrollView maxH="560px" showsVerticalScrollIndicator={false}>
        <Box px="18px" py="18px">
          {/* Attending */}
          <Box mb="28px">
            <Text fontSize="14px" fontWeight="600" color="#6B7280" mb="16px">
              {`${Loc.currentLang.gameEvent.attending} (${attendingPlayers.length})`}
            </Text>

            {renderPlayers(
              attendingPlayers,
              Loc.currentLang.gameEvent.noAttendingPlayersFound,
            )}
          </Box>

          <Box>
            <Text fontSize="14px" fontWeight="600" color="#6B7280" mb="16px">
              {`${Loc.currentLang.gameEvent.notAttending} (${notAttendingPlayers.length})`}
            </Text>

            {renderPlayers(
              notAttendingPlayers,
              Loc.currentLang.gameEvent.noNotAttendingPlayersFound,
            )}
          </Box>
        </Box>
      </ScrollView>
    </>
  );
};

export default Attendance;
